import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { GET_TMS_VEHICLES_WITH_MODEL, TmsVehiclesWithModelResponse } from '../schedule/graphql/queries/getTmsVehiclesWithModel';
import { TmsVehicleStatus } from 'enums/TmsEnum';

/**
 * 根据 carrierId 自动查询车辆数据的 Hook
 * @param carrierId 承运商ID（可以是 string、number 或 null）
 * @param skip 是否跳过查询（可选，默认为 false）
 * @returns 返回车辆数据和选项
 */
export const useTmsVehiclesByCarrier = (
    carrierId: number | string | null | undefined,
    skip: boolean = false
) => {
    // 根据 carrierId 查询车辆数据
    // status: 1 表示 AVAILABLE（可用状态），不查询 SUSPENDED（停用）状态的车辆
    const { data: vehiclesData, loading: vehiclesLoading } = useQuery<TmsVehiclesWithModelResponse>(
        GET_TMS_VEHICLES_WITH_MODEL,
        {
            variables: {
                carrierId: carrierId ? Number(carrierId) : null,
                status: TmsVehicleStatus.AVAILABLE // 只查询可用状态的车辆，不查询 SUSPENDED 状态的车辆
            },
            // 当 carrierId 为空时或 skip 为 true 时，不查询（避免查询所有车辆）
            skip: !carrierId || skip
        }
    );

    // 生成车辆选项
    const vehicleOptions = useMemo(() => {
        // 显示该承运商下的所有车辆（无论司机是否绑定了车辆）
        // 后端已支持通过 carrierId 过滤，直接使用返回的数据
        if (vehiclesData?.getTmsVehiclesWithModel) {
            return vehiclesData.getTmsVehiclesWithModel
                .map(vehicle => ({
                    label: vehicle.licensePlate || '',
                    value: vehicle.id.toString()
                }));
        }
        
        // 如果没有车辆数据，返回空数组
        return [];
    }, [vehiclesData]);

    return {
        vehiclesData,
        vehicleOptions,
        vehiclesLoading
    };
};
